import type { RumEvent, RumEventDomainContext } from "@datadog/browser-rum";

export const SESSION_SAMPLE_RATE = 100;
export const SESSION_REPLAY_SAMPLE_RATE = 20;
export const HIDDEN_TAB_REPLAY_PAUSE_MS = 5 * 60 * 1000;

const AUTH0_REAUTH_CODES = new Set([
  "login_required",
  "invalid_grant",
  "missing_refresh_token",
]);

function isAuth0ReauthShaped(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  const e = err as Record<string, unknown>;
  return typeof e["error"] === "string" && AUTH0_REAUTH_CODES.has(e["error"]);
}

/**
 * Datadog RUM `beforeSend` filter.
 *
 * Rules (matching psr-tool strict shape-based filtering):
 * - Keep every non-error event.
 * - Drop error events when the caller passed `customContext.expectedAuthError: true`.
 * - Drop error events whose original error (or one level of `.cause`) has an Auth0
 *   reauth shape `{ error: "login_required" | "invalid_grant" | "missing_refresh_token" }`.
 * - Keep plain `Error("login_required")` — string matching alone is not a signal.
 */
export function shouldSendDatadogEvent(
  event: RumEvent,
  context: RumEventDomainContext,
): boolean {
  if (event.type !== "error") return true;

  // Check caller-supplied customContext.expectedAuthError flag
  const eventCtx = (event as { context?: Record<string, unknown> }).context;
  const customCtx =
    eventCtx?.["customContext"] != null &&
    typeof eventCtx["customContext"] === "object"
      ? (eventCtx["customContext"] as Record<string, unknown>)
      : null;
  if (customCtx?.["expectedAuthError"] === true) return false;

  // Drop if the original error (or its .cause) has Auth0 reauth shape
  const originalError = (context as { error?: unknown }).error;
  if (isAuth0ReauthShaped(originalError)) return false;
  if (
    originalError instanceof Error &&
    isAuth0ReauthShaped((originalError as Error & { cause?: unknown }).cause)
  )
    return false;

  return true;
}

export function isDatadogRumConfigured(): boolean {
  return (
    Boolean(process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID?.trim()) &&
    Boolean(process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN?.trim())
  );
}

/**
 * Mirrors createApiUrl's port-omission logic so the match is always consistent
 * with the absolute URLs that authenticatedClient actually fetches.
 * Returns an empty array when NEXT_PUBLIC_API_HOST is not set.
 */
export function buildAllowedTracingUrls(): Array<{
  match: (url: string) => boolean;
  propagatorTypes: ["tracecontext"];
}> {
  const protocol = process.env.NEXT_PUBLIC_API_PROTOCOL ?? "https";
  const host = process.env.NEXT_PUBLIC_API_HOST?.trim();
  const port = process.env.NEXT_PUBLIC_API_PORT;
  if (!host) return [];

  const showPort =
    !!port &&
    !(
      (protocol === "https" && port === "443") ||
      (protocol === "http" && port === "80")
    );
  const origin = `${protocol}://${host}${showPort ? `:${port}` : ""}`;

  const originWithSlash = `${origin}/`;

  return [
    {
      match: (url: string) =>
        url === origin || url.startsWith(originWithSlash),
      propagatorTypes: ["tracecontext"],
    },
  ];
}
